import {
  ChevronRight,
  CircleAlert,
  ExternalLink,
  Link,
  LoaderCircle,
} from 'lucide-react';
import { Badge, Menu, MenuItem } from '@/components/ui';
import { SmartSummaryBlock } from '../SmartSummaryBlock';
import { SourceInfo } from '../SourceInfo';
import { formatReceivedTimestamp } from '@/lib/date';
import { isReadingStatus } from '@/lib/emailReadState';
import { ADD_TO_YARUTA_COPY } from '@/constants/addToYarutaCopy';
import type { UploadKind } from '@/lib/uploadKind';

export type EmailStatus =
  | 'RECEIVED'
  | 'PROCESSING'
  | 'HELD'
  | 'REVIEW_REQUIRED'
  | 'PROCESSED'
  | 'ERROR';

// RECEIVED / PROCESSING / HELD render the Figma "Reading" state (YAR-477,
// Review Card Status=reading, 10993:6003) — see `isReadingStatus`.

/** Pills for the statuses that keep the default card layout. */
const STATUS_BADGE: Partial<
  Record<EmailStatus, { label: string; variant: 'warning' | 'success' }>
> = {
  REVIEW_REQUIRED: { label: 'Needs Review', variant: 'warning' },
  PROCESSED: { label: 'Approved', variant: 'success' },
};

export interface ReviewCardProps {
  subject: string;
  receivedAt: string;
  summary?: string;
  sender: string;
  /** See `SourceInfo` — a relayed message names its relayer (YAR-427). */
  isForwarded?: boolean;
  forwardedBy?: string | null;
  /** See `SourceInfo` — names the school or gym behind a platform relay. */
  senderOrg?: string | null;
  provider?: string;
  attachmentCount?: number;
  /** URLs extracted from the email body. When length >= 1 a chip is shown. */
  links?: string[];
  extractedItemCount: number;
  isUnread?: boolean;
  status?: EmailStatus;
  onClick?: () => void;
  /** Couldn't read: re-read this email. */
  onRetry?: () => void;
  /** Couldn't read: open the discard confirmation (YAR-140). */
  onDiscard?: () => void;
  /**
   * A Try again is in flight. The button leaves while it runs so it cannot
   * be pressed twice (YAR-480 AC8); the card flips to Reading once the
   * server confirms.
   */
  retrying?: boolean;
  /**
   * Just opened from the email status toast's Review (YAR-480 AC7): the
   * run's new cards are marked for a moment so the parent can find them.
   */
  highlighted?: boolean;
  /**
   * A photo or file the parent added (YAR-556): the source row reads PHOTO /
   * FILE over "Added by you", and while it is read the title is "Your photo"
   * (Figma `.Review Card Content` Source=photo).
   */
  upload?: UploadKind | null;
}

/** One ring, only while highlighted — never alongside another ring colour. */
const HIGHLIGHT_CLASS = 'ring-2 ring-primary';

export function ReviewCard({
  subject,
  receivedAt,
  summary,
  sender,
  isForwarded = false,
  forwardedBy = null,
  senderOrg = null,
  provider,
  attachmentCount,
  links,
  extractedItemCount,
  isUnread = false,
  status,
  onClick,
  onRetry,
  onDiscard,
  retrying = false,
  highlighted = false,
  upload = null,
}: ReviewCardProps) {
  if (isReadingStatus(status)) {
    return (
      <ReadingCard
        highlighted={highlighted}
        // Figma Source=photo, Status=reading titles the card by what it is.
        subject={upload ? ADD_TO_YARUTA_COPY.readingCardTitle(upload) : subject}
        receivedAt={receivedAt}
      />
    );
  }
  if (status === 'ERROR') {
    return (
      <CouldntReadCard
        highlighted={highlighted}
        subject={subject}
        receivedAt={receivedAt}
        onClick={onClick}
        onRetry={onRetry}
        onDiscard={onDiscard}
        retrying={retrying}
      />
    );
  }

  const badge = status ? (STATUS_BADGE[status] ?? null) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      data-highlighted={highlighted || undefined}
      className={[
        'w-full text-left bg-card border border-border-strong rounded-xl p-5 cursor-pointer hover:shadow-hover-neutral transition-shadow',
        highlighted ? HIGHLIGHT_CLASS : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {isUnread && (
            <span
              data-testid="review-card-unread"
              className="w-2 h-2 rounded-full bg-primary shrink-0"
            />
          )}
          <h4 className="text-base font-bold text-text-body truncate">
            {subject}
          </h4>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {badge && (
            <Badge
              variant={badge.variant}
              size="sm"
            >
              {badge.label}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {formatReceivedTimestamp(receivedAt)}
          </span>
        </div>
      </div>

      {summary && (
        <SmartSummaryBlock
          summary={summary}
          className="mb-3"
        />
      )}

      {/* `items-end` is what puts "Review N items ›" on the SAME line as the
          attachment count rather than on a row of its own (YAR-131, third
          pass): SourceInfo's last line is the attachments one, so aligning
          both children to the bottom lands them on it. With no attachments
          the count falls back to the sender's line, which is the next
          sensible anchor — Figma has no mock for that case. */}
      <div className="flex items-end justify-between gap-2 mb-3">
        <SourceInfo
          provider={provider}
          sender={sender}
          attachmentCount={attachmentCount}
          isForwarded={isForwarded}
          forwardedBy={forwardedBy}
          senderOrg={senderOrg}
          upload={upload}
        />
        {extractedItemCount > 0 && (
          <div className="flex items-center gap-1 shrink-0 text-sm font-semibold text-muted-foreground">
            Review {extractedItemCount} item{extractedItemCount > 1 ? 's' : ''}
            <ChevronRight size={14} />
          </div>
        )}
      </div>

      {links && links.length > 0 && (
        <div
          className="mb-3"
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          <Menu
            align="left"
            trigger={
              <span
                role="button"
                tabIndex={0}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-info-bg text-info-text text-xs font-medium border border-info-border cursor-pointer hover:bg-info-bg/80"
                aria-label={`${links.length} link${links.length > 1 ? 's' : ''} in email`}
              >
                <Link size={12} />
                {links.length} link{links.length > 1 ? 's' : ''}
              </span>
            }
          >
            {links.map((url) => (
              <MenuItem
                key={url}
                icon={<ExternalLink size={14} />}
                onClick={() => {
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                <span className="truncate max-w-[260px] block">{url}</span>
              </MenuItem>
            ))}
          </Menu>
        </div>
      )}
    </button>
  );
}

/**
 * Shared chrome of the two status cards (Figma `.Card` wrapping
 * Status=reading / Status=couldn't read): the default card's surface, with
 * every row stacked on the 12px `spacing/component/md` gap.
 */
const STATUS_CARD_CLASS =
  'w-full text-left bg-card border border-border-strong rounded-xl p-5 flex flex-col gap-3';

/** Pill chrome shared by Reading and Couldn't read; colours are per state. */
const STATUS_PILL_CLASS =
  'self-start inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-normal whitespace-nowrap';

function StatusCardHeading({
  subject,
  receivedAt,
  titleColorClass,
}: {
  subject: string;
  receivedAt: string;
  /** Exactly one colour utility — Reading greys the title, Couldn't read does not. */
  titleColorClass: 'text-text-placeholder' | 'text-text-heading';
}) {
  return (
    <div className="flex items-start gap-5 w-full">
      <h4
        className={[
          'flex-1 min-w-0 m-0 text-base font-bold break-words',
          titleColorClass,
        ].join(' ')}
      >
        {subject}
      </h4>
      <span className="shrink-0 whitespace-nowrap text-xs font-medium text-muted-foreground">
        {formatReceivedTimestamp(receivedAt)}
      </span>
    </div>
  );
}

/**
 * Figma Review Card Status=reading (10993:6003): an email Yaruta is still
 * reading. Not a button — there is nothing to open until the read finishes,
 * and the Smart Summary and footer are replaced by one line.
 */
function ReadingCard({
  subject,
  receivedAt,
  highlighted,
}: {
  subject: string;
  receivedAt: string;
  highlighted: boolean;
}) {
  return (
    <div
      data-highlighted={highlighted || undefined}
      className={[STATUS_CARD_CLASS, highlighted ? HIGHLIGHT_CLASS : '']
        .filter(Boolean)
        .join(' ')}
      aria-busy="true"
    >
      <span
        className={[
          STATUS_PILL_CLASS,
          'border-info-border bg-info-bg text-info-text',
        ].join(' ')}
      >
        <LoaderCircle
          size={16}
          strokeWidth={1.5}
          aria-hidden="true"
          className="shrink-0 motion-safe:animate-spin"
        />
        Reading
      </span>
      <StatusCardHeading
        subject={subject}
        receivedAt={receivedAt}
        titleColorClass="text-text-placeholder"
      />
      <p className="m-0 text-sm font-normal text-text-placeholder">
        Items will appear here once it&apos;s read.
      </p>
    </div>
  );
}

/**
 * Figma Review Card Status=couldn't read (10993:6035). Discard (tertiary,
 * error colour) and Try again (secondary, rightmost) stay after any number
 * of failures (YAR-480 AC9) — only the toast swaps to See email.
 *
 * The card body still opens the email when `onClick` is given: that screen
 * keeps Open in Gmail and add-by-hand, which is where YAR-171 AC5 sends a
 * parent after a second failure.
 */
function CouldntReadCard({
  subject,
  receivedAt,
  onClick,
  onRetry,
  onDiscard,
  retrying,
  highlighted,
}: {
  highlighted: boolean;
  subject: string;
  receivedAt: string;
  onClick?: () => void;
  onRetry?: () => void;
  onDiscard?: () => void;
  retrying: boolean;
}) {
  return (
    <div
      data-highlighted={highlighted || undefined}
      className={[
        STATUS_CARD_CLASS,
        onClick ? 'cursor-pointer' : '',
        highlighted ? HIGHLIGHT_CLASS : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      role="group"
      aria-label={subject}
    >
      <span
        className={[
          STATUS_PILL_CLASS,
          'border-border-error bg-danger-light text-text-error',
        ].join(' ')}
      >
        <CircleAlert
          size={16}
          strokeWidth={1.5}
          aria-hidden="true"
          className="shrink-0"
        />
        Couldn&apos;t read
      </span>
      <StatusCardHeading
        subject={subject}
        receivedAt={receivedAt}
        titleColorClass="text-text-heading"
      />
      <p className="m-0 text-sm font-normal text-muted-foreground">
        This happens sometimes. Try it again.
      </p>
      {(onDiscard || (onRetry && !retrying)) && (
        <div className="flex items-center justify-end gap-3 w-full">
          {onDiscard && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDiscard();
              }}
              className="inline-flex items-center justify-center px-1.5 py-1 rounded-md border-0 bg-transparent text-sm font-semibold text-text-error cursor-pointer focus-visible:outline-2 focus-visible:outline-border-focus"
            >
              Discard
            </button>
          )}
          {onRetry && !retrying && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
              className="inline-flex items-center justify-center px-1.5 py-1 rounded-md border border-border bg-card text-sm font-semibold text-muted-foreground cursor-pointer focus-visible:outline-2 focus-visible:outline-border-focus"
            >
              Try again
            </button>
          )}
        </div>
      )}
    </div>
  );
}
